<template>
  <div>
    <button
      v-if="!expanded && !hasExistingLabel"
      class="bg-blue-600 text-white px-5 py-3 rounded-full shadow-lg hover:bg-blue-700 floating-label-btn"
      @click="openModal"
    >
      Create Shipping Label
    </button>
    <button
      v-else-if="!expanded"
      class="text-xs text-gray-500 underline hover:text-gray-700 mt-2"
      @click="openModal"
    >
      Create another label
    </button>

    <BaseModal v-model="expanded" class="label-creator-modal">
      <div class="modal-layout">
        <div class="modal-head border-b pb-2 mb-3">
          <div class="text-lg font-bold">Create Shipping Label</div>
          <div class="text-sm text-gray-600 flex gap-3 mt-1">
            <span v-if="orderKey"><b>Order:</b> {{ orderKey }}</span>
            <span v-if="shippingName"><b>Ship to:</b> {{ shippingName }}</span>
          </div>
        </div>

        <div class="modal-body grid grid-cols-2 gap-4">
          <!-- LEFT: configuration -->
          <div v-if="!shipResult" class="modal-col space-y-4 pr-2">
            <!-- Ship From -->
            <div v-if="shipFromAddresses.length > 0">
              <label class="block text-sm font-bold mb-1">Ship From</label>
              <select
                v-model="selectedShipFromId"
                class="border rounded px-2 py-1 w-full"
                :disabled="busy"
              >
                <option
                  v-for="a in shipFromAddresses"
                  :key="a.id"
                  :value="a.id"
                >
                  {{ a.label }} — {{ a.addressLine1 }}, {{ a.cityLocality }}
                  {{ a.stateProvince }}
                </option>
              </select>
            </div>
            <div v-else-if="!loadingShipFrom" class="text-red-500 text-sm">
              No ship-from addresses configured for this department.
            </div>

            <!-- Packages -->
            <div>
              <div class="flex justify-between items-center mb-2">
                <span class="font-bold">Packages ({{ packages.length }})</span>
                <button
                  class="dark-button text-xs"
                  :disabled="busy"
                  @click="addPackage"
                >
                  + Add Package
                </button>
              </div>

              <div
                v-for="(pkg, idx) in packages"
                :key="idx"
                class="border rounded p-2 mb-2"
              >
                <div class="flex justify-between items-center mb-1">
                  <span class="text-xs font-bold">Package {{ idx + 1 }}</span>
                  <button
                    v-if="packages.length > 1"
                    class="text-red-500 text-xs"
                    :disabled="busy"
                    @click="removePackage(idx)"
                  >
                    remove
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-2 mb-1">
                  <div>
                    <label class="text-xs">lbs</label>
                    <input
                      v-model.number="pkg.weightLbs"
                      type="number"
                      min="0"
                      class="border rounded px-3 py-2 w-full text-base"
                      :disabled="busy"
                    />
                  </div>
                  <div>
                    <label class="text-xs">oz</label>
                    <input
                      v-model.number="pkg.weightOz"
                      type="number"
                      min="0"
                      class="border rounded px-3 py-2 w-full text-base"
                      :disabled="busy"
                    />
                  </div>
                </div>
                <div class="text-xs text-gray-500 mb-1">
                  Total:
                  {{ (pkg.weightLbs || 0) * 16 + (pkg.weightOz || 0) }} oz
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <label class="text-xs">L (in)</label>
                    <input
                      v-model.number="pkg.length"
                      type="number"
                      min="0"
                      class="border rounded px-3 py-2 w-full text-base"
                      :disabled="busy"
                    />
                  </div>
                  <div>
                    <label class="text-xs">W (in)</label>
                    <input
                      v-model.number="pkg.width"
                      type="number"
                      min="0"
                      class="border rounded px-3 py-2 w-full text-base"
                      :disabled="busy"
                    />
                  </div>
                  <div>
                    <label class="text-xs">H (in)</label>
                    <input
                      v-model.number="pkg.height"
                      type="number"
                      min="0"
                      class="border rounded px-3 py-2 w-full text-base"
                      :disabled="busy"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-2">
              <button
                class="dark-button flex-1"
                :disabled="busy || !hasValidPackage"
                @click="savePackages"
              >
                {{ savingPackages ? "Saving..." : "Save Packages" }}
              </button>
              <button
                class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-1"
                :disabled="busy || !hasValidPackage"
                @click="fetchRates"
              >
                {{ loadingRates ? "Loading rates..." : "Get Shipping Rates" }}
              </button>
            </div>
          </div>

          <!-- RIGHT: rates + result -->
          <div
            class="modal-col pl-2 space-y-4"
            :class="shipResult ? 'col-span-2' : 'border-l'"
          >
            <div
              v-if="!shipResult && rates.length === 0 && !loadingRates"
              class="text-sm text-gray-500 italic"
            >
              Get shipping rates to see options here.
            </div>

            <!-- Rates -->
            <div v-if="!shipResult && rates.length > 0">
              <div class="font-bold text-sm mb-1">
                {{ filteredRates.length }} rate(s)
                <span class="text-xs font-normal text-gray-500">
                  — sorted by price
                </span>
              </div>

              <!-- Carrier tabs -->
              <div class="flex gap-1 flex-wrap mb-2">
                <button
                  class="px-2 py-1 text-xs rounded border"
                  :class="
                    rateTab === 'all'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-gray-700'
                  "
                  @click="rateTab = 'all'"
                >
                  All
                </button>
                <button
                  v-for="ct in carrierTabs"
                  :key="ct.id"
                  class="px-2 py-1 text-xs rounded border"
                  :class="
                    rateTab === ct.id
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-gray-700'
                  "
                  @click="rateTab = ct.id"
                >
                  {{ ct.label }}
                </button>
              </div>

              <div class="border rounded divide-y max-h-80 overflow-auto mb-2">
                <div
                  v-for="rate in filteredRates"
                  :key="rate.rateId"
                  class="p-2 cursor-pointer flex items-center justify-between"
                  :class="
                    selectedRateId === rate.rateId
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-100'
                  "
                  @click="!busy && (selectedRateId = rate.rateId)"
                >
                  <div class="flex-1">
                    <div class="font-medium text-sm">
                      {{ rate.serviceType }}
                      <span
                        v-if="
                          rate.packageType && rate.packageType !== 'package'
                        "
                        class="text-xs px-1 border rounded ml-1"
                      >
                        {{ rate.packageType.replace(/_/g, " ") }}
                      </span>
                    </div>
                    <div class="text-xs">
                      {{ rate.carrierFriendlyName }}
                      <span v-if="rate.carrierNickname">
                        ({{ rate.carrierNickname }})
                      </span>
                      — {{ rate.deliveryDays }} day(s)
                      <span v-if="rate.estimatedDeliveryDate">
                        · ETA {{ formatEta(rate.estimatedDeliveryDate) }}
                      </span>
                    </div>
                    <div v-if="rateBadges[rate.rateId]" class="text-xs mt-1">
                      <span
                        v-for="badge in rateBadges[rate.rateId]"
                        :key="badge"
                        class="inline-block px-1 mr-1 rounded text-white"
                        :class="
                          badge === 'cheapest'
                            ? 'bg-green-600'
                            : badge === 'fastest'
                            ? 'bg-orange-500'
                            : 'bg-blue-500'
                        "
                      >
                        {{ badge }}
                      </span>
                    </div>
                  </div>
                  <div class="text-right ml-2">
                    <div class="font-bold">
                      ${{ totalCost(rate).toFixed(2) }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="rateErrors.length > 0"
                class="text-xs text-red-500 mb-2"
              >
                <div v-for="(err, i) in rateErrors" :key="i">
                  {{ err.carrierName || err.carrierCode }}: {{ err.message }}
                </div>
              </div>

              <button
                v-if="selectedRateId"
                class="bg-green-600 text-white w-full py-3 rounded shadow-lg hover:bg-green-700 sticky-purchase-btn"
                :disabled="busy"
                @click="purchaseLabel"
              >
                {{ shipping ? "Purchasing label..." : "Purchase Label & Ship" }}
              </button>
            </div>

            <!-- Result -->
            <div
              v-if="shipResult"
              class="bg-green-100 border border-green-400 rounded p-3 text-sm"
            >
              <div class="font-bold mb-1">Order shipped!</div>
              <div>Tracking: {{ shipResult.trackingNumber }}</div>
              <div>
                Carrier: {{ shipResult.carrierCode }} /
                {{ shipResult.serviceCode }}
              </div>
              <div class="flex gap-2 mt-2">
                <a
                  v-if="shipResult.labelDownload && shipResult.labelDownload.pdf"
                  class="dark-button"
                  :href="shipResult.labelDownload.pdf"
                  target="_blank"
                >
                  Download PDF
                </a>
                <button
                  class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  :disabled="printingLabel"
                  @click="sendLabelToPrintNode"
                >
                  {{ printingLabel ? "Sending..." : "Print on PrintNode" }}
                </button>
              </div>

              <div
                v-if="
                  shipResult.labelDownload &&
                  (shipResult.labelDownload.png || shipResult.labelDownload.pdf)
                "
                class="mt-3 border bg-white rounded overflow-hidden"
              >
                <img
                  v-if="shipResult.labelDownload.png"
                  :src="shipResult.labelDownload.png"
                  alt="Shipping label"
                  class="w-full h-auto"
                />
                <iframe
                  v-else
                  :src="shipResult.labelDownload.pdf"
                  class="w-full"
                  style="height: 600px; border: 0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import ShippingApi from "@/api/Shipping.js";
import PrintNodeApi from "@/api/PrintNode.js";

export default {
  props: {
    orderUUID: { type: String, required: true },
    departamentoId: { type: String, default: null },
    initialPackages: { type: Array, default: () => [] },
    orderKey: { type: String, default: "" },
    shippingName: { type: String, default: "" },
    hasExistingLabel: { type: Boolean, default: false }
  },
  data() {
    return {
      expanded: false,
      shipFromAddresses: [],
      loadingShipFrom: false,
      selectedShipFromId: null,

      packages: [],

      savingPackages: false,
      loadingRates: false,
      shipping: false,

      rates: [],
      rateErrors: [],
      selectedRateId: null,
      rateTab: "all",

      shipResult: null,
      printingLabel: false
    };
  },
  computed: {
    busy() {
      return this.savingPackages || this.loadingRates || this.shipping;
    },
    hasValidPackage() {
      return this.packages.some(
        p => (p.weightLbs || 0) * 16 + (p.weightOz || 0) > 0
      );
    },
    carrierTabs() {
      const map = new Map();
      for (const r of this.rates) {
        if (!map.has(r.carrierId)) {
          map.set(r.carrierId, {
            id: r.carrierId,
            code: r.carrierCode,
            label: r.carrierNickname
              ? `${r.carrierFriendlyName} (${r.carrierNickname})`
              : r.carrierFriendlyName
          });
        }
      }
      return Array.from(map.values());
    },
    filteredRates() {
      let list = this.rates.slice();
      if (this.rateTab !== "all") {
        list = list.filter(r => r.carrierId === this.rateTab);
      }
      return list.sort((a, b) => this.totalCost(a) - this.totalCost(b));
    },
    rateBadges() {
      const badges = {};
      if (this.rates.length === 0) return badges;
      let cheapest = null;
      let fastest = null;
      for (const r of this.rates) {
        const cost = this.totalCost(r);
        const days = Number(r.deliveryDays) || Infinity;
        if (!cheapest || cost < this.totalCost(cheapest)) cheapest = r;
        if (
          !fastest ||
          days < (Number(fastest.deliveryDays) || Infinity) ||
          (days === (Number(fastest.deliveryDays) || Infinity) &&
            cost < this.totalCost(fastest))
        ) {
          fastest = r;
        }
      }
      if (cheapest) badges[cheapest.rateId] = ["cheapest"];
      if (fastest && fastest.rateId !== (cheapest && cheapest.rateId)) {
        badges[fastest.rateId] = ["fastest"];
      } else if (fastest && cheapest && fastest.rateId === cheapest.rateId) {
        badges[fastest.rateId] = ["cheapest", "fastest"];
      }
      return badges;
    }
  },
  watch: {
    expanded(val) {
      if (val) this.init();
      document.body.style.overflow = val ? "hidden" : "";
    }
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
  created() {
    this.packages = this.buildInitialPackages(this.initialPackages);
  },
  methods: {
    buildInitialPackages(dbPkgs) {
      if (!Array.isArray(dbPkgs) || dbPkgs.length === 0) {
        return [
          {
            weightLbs: 0,
            weightOz: 0,
            length: null,
            width: null,
            height: null
          }
        ];
      }
      return dbPkgs.map(p => {
        const totalOz = Number(p.weightOz) || 0;
        return {
          weightLbs: Math.floor(totalOz / 16),
          weightOz: Math.round(totalOz % 16),
          length: p.lengthIn ? Number(p.lengthIn) : null,
          width: p.widthIn ? Number(p.widthIn) : null,
          height: p.heightIn ? Number(p.heightIn) : null
        };
      });
    },
    openModal() {
      this.expanded = true;
    },
    async init() {
      if (this.shipFromAddresses.length === 0 && this.departamentoId) {
        await this.loadShipFromAddresses();
      }
    },
    async loadShipFromAddresses() {
      this.loadingShipFrom = true;
      try {
        const { data } = await ShippingApi.getShipFromAddresses(
          this.departamentoId
        );
        this.shipFromAddresses = Array.isArray(data) ? data : [];
        const def = this.shipFromAddresses.find(a => a.isDefault === 1);
        this.selectedShipFromId = def
          ? def.id
          : this.shipFromAddresses[0]
          ? this.shipFromAddresses[0].id
          : null;
      } catch (e) {
        this.$toast.open({
          message: "Error loading ship-from addresses",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loadingShipFrom = false;
      }
    },
    addPackage() {
      this.packages.push({
        weightLbs: 0,
        weightOz: 0,
        length: null,
        width: null,
        height: null
      });
    },
    removePackage(idx) {
      this.packages.splice(idx, 1);
    },
    buildPackagesPayload() {
      return this.packages.map(p => ({
        weight_oz: (p.weightLbs || 0) * 16 + (p.weightOz || 0),
        length_in: p.length || null,
        width_in: p.width || null,
        height_in: p.height || null
      }));
    },
    async savePackages() {
      this.savingPackages = true;
      try {
        await ShippingApi.savePackages(
          this.orderUUID,
          this.buildPackagesPayload()
        );
        this.$toast.open({
          message: `${this.packages.length} package(s) saved`,
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        this.$toast.open({
          message: "Error saving packages",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.savingPackages = false;
      }
    },
    async fetchRates() {
      this.loadingRates = true;
      this.rates = [];
      this.rateErrors = [];
      this.selectedRateId = null;
      try {
        await ShippingApi.savePackages(
          this.orderUUID,
          this.buildPackagesPayload()
        );

        const params = {};
        if (this.selectedShipFromId) {
          params.ship_from_address_id = this.selectedShipFromId;
        }
        const { data } = await ShippingApi.getRates(this.orderUUID, params);
        if (data.success && data.rates) {
          const rr = data.rates.rateResponse;
          const rateList =
            (rr && rr.rates) || (Array.isArray(data.rates) ? data.rates : []);
          this.rates = rateList || [];
          this.rateErrors = (rr && rr.errors) || [];
          this.rateTab = "all";
          if (this.rates.length > 0) {
            this.$toast.open({
              message: `${this.rates.length} rates found`,
              type: "success",
              position: "top-right"
            });
          } else {
            this.$toast.open({
              message: "No rates returned",
              type: "info",
              position: "top-right"
            });
          }
        } else {
          this.$toast.open({
            message: data.message || "Failed to get rates",
            type: "error",
            position: "top-right"
          });
        }
      } catch (e) {
        this.$toast.open({
          message: "Failed to fetch shipping rates",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loadingRates = false;
      }
    },
    async purchaseLabel() {
      if (!this.selectedRateId) return;
      if (!confirm("Purchase shipping label and mark this order as shipped?")) {
        return;
      }
      this.shipping = true;
      try {
        const { data } = await ShippingApi.shipOrder(
          this.orderUUID,
          this.selectedRateId
        );
        if (data.success) {
          this.shipResult = data;
          this.$toast.open({
            message: `Shipped! Tracking: ${data.trackingNumber}`,
            type: "success",
            position: "top-right"
          });
          this.$emit("labelCreated", data);
        } else {
          const carrierMsg =
            (data.shipstationError &&
              data.shipstationError.body &&
              data.shipstationError.body.errors &&
              data.shipstationError.body.errors[0] &&
              data.shipstationError.body.errors[0].message) ||
            data.message ||
            "Shipping failed";
          this.$toast.open({
            message: carrierMsg,
            type: "error",
            position: "top-right"
          });
        }
      } catch (err) {
        const resp = err && err.response && err.response.data;
        const carrierMsg =
          (resp &&
            resp.shipstationError &&
            resp.shipstationError.body &&
            resp.shipstationError.body.errors &&
            resp.shipstationError.body.errors[0] &&
            resp.shipstationError.body.errors[0].message) ||
          (resp && resp.message) ||
          "Failed to ship order";
        this.$toast.open({
          message: carrierMsg,
          type: "error",
          position: "top-right"
        });
      } finally {
        this.shipping = false;
      }
    },
    async sendLabelToPrintNode() {
      this.printingLabel = true;
      try {
        const shipmentUuid = this.shipResult && this.shipResult.shipmentUuid;
        if (!shipmentUuid) {
          throw new Error("Missing shipment_uuid in ship result");
        }
        const { data } = await PrintNodeApi.printLabel(shipmentUuid);
        if (data && data.error) {
          this.$toast.open({
            message: data.error,
            type: "error",
            position: "top-right"
          });
        } else {
          this.$toast.open({
            message: "Label sent to printer",
            type: "success",
            position: "top-right"
          });
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.error) ||
          "Failed to send label to PrintNode";
        this.$toast.open({
          message: msg,
          type: "error",
          position: "top-right"
        });
      } finally {
        this.printingLabel = false;
      }
    },
    totalCost(rate) {
      const ship = (rate.shippingAmount && rate.shippingAmount.amount) || 0;
      const other = (rate.otherAmount && rate.otherAmount.amount) || 0;
      return Number(ship) + Number(other);
    },
    formatEta(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString();
    }
  }
};
</script>

<style>
.floating-label-btn {
  position: sticky;
  bottom: 16px;
  margin-left: auto;
  margin-right: 16px;
  z-index: 5;
}
.label-creator-modal.modal {
  padding-top: 0 !important;
  display: none;
  z-index: 1000 !important;
}
.label-creator-modal.modal.active {
  display: flex !important;
  align-items: stretch;
  justify-content: center;
}
.label-creator-modal .modal-content {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  border-radius: 0;
  border: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.label-creator-modal .modal-content > .close {
  position: absolute;
  top: 8px;
  right: 16px;
  z-index: 10;
}
.label-creator-modal .modal-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 20px 24px;
}
.label-creator-modal .modal-body {
  flex: 1;
  min-height: 0;
}
.label-creator-modal .modal-col {
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
  position: relative;
}
.sticky-purchase-btn {
  position: sticky;
  bottom: 0;
  z-index: 5;
}
</style>
